import React, { useContext } from "react";

import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import { makeStyles } from "@material-ui/core/styles";
import ArrowBackRoundedIcon from "@material-ui/icons/ArrowBackRounded";
import ArrowForwardRoundedIcon from "@material-ui/icons/ArrowForwardRounded";

import { SetupContext } from "../../pages/SetupFlow/context/SetupContext";
import { getFlowSteps } from "../../pages/SetupFlow/flowConfig";

const useStyles = makeStyles(theme => ({
  page: {
    width: "100%",
    height: "100%",
    minHeight: 0,
    boxSizing: "border-box",
    padding: "28px 20px",
    display: "flex",
    justifyContent: "center",
    overflow: "hidden",
    overscrollBehavior: "none",
    color: theme.palette.text.primary,
    background:
      theme.palette.type === "dark"
        ? "#08101F"
        : "#F4F7FB",

    [theme.breakpoints.down("sm")]: {
      width: "100%",
      height: "100%",
      maxHeight: "100%",
      minHeight: 0,
      padding: 0,
      overflow: "hidden",
      background: theme.palette.background.paper
    }
  },

  shell: {
    width: "100%",
    maxWidth: 1180,
    flex: 1,
    height: "100%",
    maxHeight: "100%",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: 24,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper,
    boxShadow:
      theme.palette.type === "dark"
        ? "0 24px 70px rgba(0,0,0,.35)"
        : "0 24px 70px rgba(15,23,42,.10)",

    [theme.breakpoints.down("sm")]: {
      width: "100%",
      flex: 1,
      height: "100%",
      maxHeight: "100%",
      minHeight: 0,
      border: 0,
      borderRadius: 0,
      boxShadow: "none",
      overflow: "hidden"
    }
  },

  topbar: {
    minHeight: 72,
    flexShrink: 0,
    padding: "16px 24px",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
    borderBottom: `1px solid ${theme.palette.divider}`,

    [theme.breakpoints.down("sm")]: {
      minHeight: 64,
      padding: "14px 16px"
    }
  },

  brand: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    minWidth: 0
  },

  brandMark: {
    width: 36,
    height: 36,
    flexShrink: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 11,
    color: "#FFFFFF",
    fontSize: 17,
    fontWeight: 900,
    background: "linear-gradient(135deg,#155EEF,#0BA5EC)",
    boxShadow: "0 8px 18px rgba(21,94,239,.24)"
  },

  brandText: {
    minWidth: 0
  },

  brandName: {
    color: theme.palette.text.primary,
    fontSize: 15,
    fontWeight: 800,
    lineHeight: 1.15,
    letterSpacing: "-.02em"
  },

  brandSubtitle: {
    marginTop: 2,
    color: theme.palette.text.secondary,
    fontSize: 11,
    lineHeight: 1.2
  },

  skipButton: {
    flexShrink: 0,
    minHeight: 36,
    padding: "6px 12px",
    borderRadius: 9,
    color: theme.palette.text.secondary,
    fontSize: 11.5,
    fontWeight: 700,
    textTransform: "none"
  },

  progressArea: {
    flexShrink: 0,
    padding: "18px 24px 0",

    [theme.breakpoints.down("sm")]: {
      padding: "15px 16px 0"
    }
  },

  progressHeader: {
    marginBottom: 8,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12
  },

  progressLabel: {
    color: theme.palette.text.secondary,
    fontSize: 11.5,
    fontWeight: 700
  },

  progressValue: {
    color: "#155EEF",
    fontSize: 11.5,
    fontWeight: 800
  },

  progressTrack: {
    width: "100%",
    height: 6,
    overflow: "hidden",
    borderRadius: 999,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.08)"
        : "rgba(15,23,42,.08)"
  },

  progressBar: {
    height: "100%",
    borderRadius: 999,
    background: "linear-gradient(90deg,#155EEF,#0BA5EC)",
    transition: "width .3s ease"
  },

  content: {
    width: "100%",
    flex: "1 1 auto",
    minHeight: 0,
    minWidth: 0,
    boxSizing: "border-box",
    padding: "32px 24px",
    overflowX: "hidden",
    overflowY: "auto",
    overscrollBehaviorY: "contain",
    WebkitOverflowScrolling: "touch",
    touchAction: "pan-y",

    [theme.breakpoints.down("sm")]: {
      minHeight: 0,
      padding: "24px 16px 32px",
      overflowX: "hidden",
      overflowY: "auto"
    }
  },

  contentInner: {
    width: "100%",
    maxWidth: 940,
    margin: "0 auto"
  },

  heading: {
    maxWidth: 720,
    marginBottom: 26
  },

  eyebrow: {
    marginBottom: 7,
    color: "#155EEF",
    fontSize: 11,
    fontWeight: 800,
    lineHeight: 1.2,
    letterSpacing: ".08em",
    textTransform: "uppercase"
  },

  title: {
    color: theme.palette.text.primary,
    fontSize: "clamp(25px,3vw,36px)",
    fontWeight: 850,
    lineHeight: 1.12,
    letterSpacing: "-.045em"
  },

  description: {
    maxWidth: 680,
    marginTop: 10,
    color: theme.palette.text.secondary,
    fontSize: 14,
    lineHeight: 1.55
  },

  footer: {
    minHeight: 76,
    flexShrink: 0,
    padding: "14px 24px",
    boxSizing: "border-box",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    borderTop: `1px solid ${theme.palette.divider}`,
    background:
      theme.palette.type === "dark"
        ? "rgba(255,255,255,.015)"
        : "#FBFCFE",

    [theme.breakpoints.down("sm")]: {
      minHeight: 72,
      flexShrink: 0,
      padding: "12px 16px",
      position: "relative",
      bottom: "auto",
      zIndex: 10
    }
  },

  footerSpacer: {
    flex: 1
  },

  button: {
    minHeight: 42,
    padding: "8px 17px",
    borderRadius: 10,
    boxShadow: "none",
    fontSize: 12.5,
    fontWeight: 800,
    textTransform: "none",

    "&:hover": {
      boxShadow: "none"
    }
  },

  backButton: {
    color: theme.palette.text.secondary,
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.paper
  },

  nextButton: {
    color: "#FFFFFF",
    background: "linear-gradient(135deg,#155EEF,#0BA5EC)",

    "&:hover": {
      background: "linear-gradient(135deg,#004EEB,#0086C9)"
    },

    "&.Mui-disabled": {
      color: "rgba(255,255,255,.72)",
      background: "rgba(21,94,239,.45)"
    }
  }
}));

export const KoraxOnboardingShell = ({
  children,
  step,
  totalSteps,
  onSkip,
  brandSubtitle = "Assistente de implantação"
}) => {
  const classes = useStyles();
  const setupContext = useContext(SetupContext);

  const activeFlow = setupContext
    ? getFlowSteps(setupContext.ai?.objective)
    : [];

  const dynamicTotal =
    activeFlow.length > 0
      ? activeFlow.length
      : Number(totalSteps) || 1;

  const dynamicStep =
    setupContext
      ? Number(setupContext.currentStep || 0) + 1
      : Number(step) || 1;

  const safeTotal = Math.max(dynamicTotal, 1);
  const safeStep = Math.min(
    Math.max(dynamicStep, 1),
    safeTotal
  );

  const progress = (safeStep / safeTotal) * 100;

  return (
    <main className={classes.page}>
      <section className={classes.shell}>
        <header className={classes.topbar}>
          <div className={classes.brand}>
            <div className={classes.brandMark}>K</div>

            <div className={classes.brandText}>
              <Typography className={classes.brandName}>
                Korax
              </Typography>

              <Typography className={classes.brandSubtitle}>
                {brandSubtitle}
              </Typography>
            </div>
          </div>

          {onSkip && (
            <Button
              className={classes.skipButton}
              onClick={onSkip}
            >
              Fazer depois
            </Button>
          )}
        </header>

        <div className={classes.progressArea}>
          <div className={classes.progressHeader}>
            <Typography className={classes.progressLabel}>
              Configuração da sua operação
            </Typography>

            <Typography className={classes.progressValue}>
              Etapa {safeStep} de {safeTotal}
            </Typography>
          </div>

          <div className={classes.progressTrack}>
            <div
              className={classes.progressBar}
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {children}
      </section>
    </main>
  );
};

export const KoraxOnboardingContent = ({
  eyebrow,
  title,
  description,
  children
}) => {
  const classes = useStyles();

  return (
    <div className={classes.content}>
      <div className={classes.contentInner}>
        {(eyebrow || title || description) && (
          <div className={classes.heading}>
            {eyebrow && (
              <Typography className={classes.eyebrow}>
                {eyebrow}
              </Typography>
            )}

            {title && (
              <Typography className={classes.title}>
                {title}
              </Typography>
            )}

            {description && (
              <Typography className={classes.description}>
                {description}
              </Typography>
            )}
          </div>
        )}

        {children}
      </div>
    </div>
  );
};

export const KoraxOnboardingFooter = ({
  onBack,
  onNext,
  backLabel = "Voltar",
  nextLabel = "Continuar",
  disableNext = false,
  hideBack = false,
  nextIcon = true
}) => {
  const classes = useStyles();

  return (
    <footer className={classes.footer}>
      {!hideBack && onBack ? (
        <Button
          className={`${classes.button} ${classes.backButton}`}
          startIcon={<ArrowBackRoundedIcon />}
          onClick={onBack}
        >
          {backLabel}
        </Button>
      ) : (
        <div className={classes.footerSpacer} />
      )}

      <Button
        className={`${classes.button} ${classes.nextButton}`}
        endIcon={nextIcon ? <ArrowForwardRoundedIcon /> : undefined}
        disabled={disableNext}
        onClick={onNext}
      >
        {nextLabel}
      </Button>
    </footer>
  );
};
